import React from 'react';
import { PageHeader } from '../components/PageHeader';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
import { ClosingCTA } from '../components/ClosingCTA';
import { DeviceMockup } from '../components/visuals/DeviceMockup';
import { MissedCallVisual } from '../components/visuals/MissedCallVisual';
import { SplitSection, ChecklistSection, ProofStrip, FaqSection, PriceSection, RelatedLinks, serviceJsonLd } from '../components/LocalPage';

/* 2026-09-17: money page 3 of 5. Target query: "missed call text back"
   (390 a month, KD 0). The blog post at
   /blog/missed-call-text-back-phoenix-home-services stays as the long
   read; this is the service page it links to. No reviews, no reputation. */

const faqs = [{
  question: 'What is missed call text back?',
  answer: 'When a call to your business goes unanswered, a text goes out to the caller automatically within seconds, from your business number, in your name. It says you missed them and asks what they need. The caller replies by text, and the conversation lands in your inbox instead of being lost.'
}, {
  question: 'Does it really work, or do people ignore the text?',
  answer: 'It works because of timing. The text arrives while the caller is still holding the phone, before they have tapped the next result. Most people find replying to a text easier than dialing a second business. It will not save every call, but it saves the ones voicemail loses.'
}, {
  question: 'What does the text say?',
  answer: 'Whatever you want it to. The default is short: your business name, sorry we missed you, what can we help with. We write it with you during setup so it sounds like you and not like a robot, and we keep it under two lines.'
}, {
  question: 'Do I need a special phone number?',
  answer: 'You need a business number that is registered for texting (the carriers call this A2P registration). Without it, texts from a business get filtered or blocked. The Viridia System includes the number and the registration, and we forward or port your existing line so callers never notice the change.'
}, {
  question: 'Does it replace an answering service?',
  answer: 'No. It is the backstop. The AI receptionist answers the call in the first place, and missed-call text-back catches anything that still gets through. Most clients run both, and they come together in the system.'
}, {
  question: 'How much does missed call text back cost?',
  answer: 'It is included in The Viridia System at $897 a month, month to month, along with the AI receptionist, the CRM, your business number and the follow-up automations. We do not sell it on its own because on its own it only catches half the problem.'
}];
const jsonLd = serviceJsonLd({
  name: 'Missed-Call Text-Back',
  serviceType: 'Missed call text back automation',
  description: 'Automatic text back to every missed call within seconds, from your business number, with the reply landing in one inbox. Part of The Viridia System, $897 a month.',
  path: '/missed-call-text-back'
});
export function MissedCallTextBackPage() {
  return <>
      <SEOHead title="Missed Call Text Back for Home Service Businesses | Viridia Analytics" description="Missed call text back sends a text to every caller you miss, in seconds, from your business number. See what it says, why callers reply, and how it fits with an AI receptionist. $897 a month, month to month." canonical="/missed-call-text-back" jsonLd={jsonLd} faq={faqs} data-id="element-2995" data-mp="1o2hjq9ej7ve3" />

      <PageHeader label="Reception" title="Missed call text back." intro="Missed call text back sends a text to the caller you just missed, in seconds, before they call the next business on the list. Here is what it says, why it works, and what it costs." data-id="element-2996" data-mp="158ls56k8v8q1">
        <div className="flex flex-wrap items-center gap-4" data-id="element-2997" data-mp="hj9loegaxerf">
          <CTAButton to="/contact" data-id="element-2998" data-mp="2j9i7rcw1wj7l">Book a 15-min call</CTAButton>
          <CTAButton to="/blog/missed-call-text-back-phoenix-home-services" variant="ghost" data-id="element-2999" data-mp="2j7xf5staracd">
            Read the long version
          </CTAButton>
        </div>
      </PageHeader>

      <SplitSection label="The problem" title="A missed call is not a message. It is a customer calling someone else." body={<>
            <p data-id="element-3001" data-mp="31zgdhmcdm082">
              Most callers do not leave voicemail. They hang up on the fourth ring and tap the next
              result. You never know they called, and they never know you would have taken the
              job.
            </p>
            <p data-id="element-3002" data-mp="3bwl1zstp27s1">
              For a home service business that is the most expensive thing that happens all day,
              and it happens quietly.
            </p>
          </>} visual={<MissedCallVisual data-id="element-3003" data-mp="1hmr60zf6bn93" />} note="The leak" data-id="element-3000" data-mp="2t261tzvnc92m" />

      <SplitSection alt flip label="How it works" title="The text lands while they are still holding the phone." body={<>
            <p data-id="element-3005" data-mp="3t8jarikh7tw2">
              The moment a call goes unanswered, a text goes out from your business number. Short,
              in your voice, asking what they need.
            </p>
            <p data-id="element-3006" data-mp="3ji6o9kt3pqna">
              They reply. The conversation lands in one inbox with your other calls and messages, and
              you or the AI receptionist picks it up from there and books it.
            </p>
          </>} visual={<DeviceMockup mode="chat" heading="Missed call, 12 seconds later" lines={[{
      from: 'system',
      text: 'Hi, this is Desert Ridge Plumbing. Sorry we missed your call. What can we help with?'
    }, {
      from: 'customer',
      text: 'Water heater is leaking from the bottom. How soon can someone come?'
    }, {
      from: 'system',
      text: 'We can be there this afternoon. What is the address?'
    }]} resultChip="Job booked from a missed call" data-id="element-3007" data-mp="3aj37inhepnby" />} note="Missed-call text-back" data-id="element-3004" data-mp="1cn4r8ljp5yy6" />

      <ChecklistSection label="Setup details" title="What has to be true for it to actually deliver." body="This is where do-it-yourself versions fall down. The text has to arrive, and it has to come from you." items={[{
      title: 'A registered business number',
      body: 'Carriers filter texts from unregistered business numbers. Your number gets A2P registration during setup, so the text lands.'
    }, {
      title: 'Your existing line keeps working',
      body: 'We forward or port your current number. Customers keep calling what is on your truck. Nothing changes on their end.'
    }, {
      title: 'Sent in seconds, not minutes',
      body: 'The window is short. The text fires the moment the call ends unanswered, while the caller is still deciding what to do next.'
    }, {
      title: 'Written in your voice',
      body: 'We draft the message with you. Your business name, plain words, two lines. No canned corporate language.'
    }, {
      title: 'One inbox for the reply',
      body: 'The reply lands next to your calls, Yelp messages and web forms in the CRM, so nothing sits in a second app you forgot to open.'
    }, {
      title: 'Follow-up if they go quiet',
      body: 'If the caller replies and then stops, a reminder goes out the next day on its own. The lead does not die because you were on a job.'
    }]} data-id="element-3008" data-mp="sae8p68fcw5o" />

      <ProofStrip items={[{
      value: '284%',
      label: 'more leads for a Phoenix landscaping company with the full system in place',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '28',
      label: 'estimates booked without the owner touching the phone',
      to: '/case-studies/tfp-landscaping',
      source: 'TFP Landscaping'
    }, {
      value: '$15k',
      label: 'closed-won work traced to AI-booked painting estimates',
      to: '/case-studies/finish-coat-painting',
      source: 'Finish Coat Painting'
    }]} data-id="element-3009" data-mp="4fucg2iqyfkc" />

      <SplitSection alt label="Where it fits" title="The backstop, not the whole plan." body={<>
            <p data-id="element-3011" data-mp="74xuy5svb04b">
              Missed call text back saves the calls that got through. The AI receptionist stops
              them getting through in the first place. Run one without the other and you are still
              leaking.
            </p>
            <p data-id="element-3012" data-mp="3bgs00ggs02yv">
              That is why it comes as part of The Viridia System instead of as a separate
              subscription.
            </p>
          </>} visual={<DeviceMockup mode="call" heading="Same caller, next time" lines={[{
      from: 'caller',
      text: 'Hi, I texted you last week about the water heater. Can I book the install?'
    }, {
      from: 'ai',
      text: 'Absolutely. I have your address on file. Does Friday at 9 AM work?'
    }]} resultChip="Answered on the first ring" data-id="element-3013" data-mp="3kchgcb3da75e" />} note="AI receptionist" data-id="element-3010" data-mp="3ubgozto4u2se" />

      <PriceSection title="Included, along with everything around it." body={<p data-id="element-3015" data-mp="23ryrx3477db6">
            Missed-call text-back, the AI receptionist, the CRM, your registered business number
            and the follow-up automations. One flat price, month to month.
          </p>} data-id="element-3014" data-mp="2igbs6cabjibh" />

      <FaqSection alt title="Questions about missed call text back." faqs={faqs} data-id="element-3016" data-mp="2wh5y8o9w976z" />

      <RelatedLinks title="Related pages" links={[{
      to: '/answering-service-for-contractors',
      label: 'Answering service for contractors',
      body: 'What answers the call before it becomes a missed one.'
    }, {
      to: '/crm-for-contractors',
      label: 'CRM for contractors',
      body: 'The inbox the reply lands in, and the follow-up that runs from there.'
    }, {
      to: '/blog/missed-call-text-back-phoenix-home-services',
      label: 'The long read',
      body: 'Why Phoenix home service businesses miss so many calls, with the math.'
    }]} data-id="element-3017" data-mp="31f7fd3nrewft" />

      <ClosingCTA data-id="element-3018" data-mp="d1o39izk399q" />
    </>;
}